// ─────────────────────────────────────────────────────────────────────────────
// Routing the Trend icon — the grid icon + the chart popup (SDMS Result Validation).
//
// The icon is a ROUTER (standing rule 8): every number in the popup is computed
// by a service that already exists — the QA/QC control-chart engine for QC series
// (control-charts/series, same limits the Control Charts screen shows) and the
// SDMS trend endpoint for a client sample's site history. This file adds no
// control-chart maths; it only decides which endpoint to call (via trendRouting)
// and renders the shared chart.
//
// The popup shows the DERIVATION at the top (QCType · QCRootRole · QCRole ·
// QCSource · Pair → route) so dropping the text label from the grid costs no
// information — a reviewer who understands the choice trusts the chart.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { api } from '../../api/client';
import { fmtLocalDate } from '../../utils/datetime';
import { mean, trimZeros, anchorToIso, sampleTrendCountPhrase, SAMPLE_TREND_MAX_POINTS } from './trendMath';
import { trendEmptyMessage } from './trendEmpty';
import { LjChart, type Pt } from '../qaqc/controlChart/ControlChartSvg';
import { THEMES, valueOf, type ChartTheme, type Field, type Stat } from '../qaqc/controlChart/controlChartData';

/** The popup always renders in the app's light chrome (a chart canvas theme, not
 *  app chrome). Pinned here so the indexed lookup is a definite ChartTheme. */
const LIGHT: ChartTheme = THEMES.light as ChartTheme;
import { fetchSeries, type ApiScope } from '../qaqc/controlChart/controlChartApi';
import type { TrendRoute, TrendPanel } from './trendRouting';
import { SiteTrendDialog, type SiteTrendProps } from './SiteTrendDialog';
import '../qaqc/controlChart/controlChart.css';
import '../analysis/sdmsEntry.css';
import './trendChart.css';

// ── The grid icon ────────────────────────────────────────────────────────────

/** One 30×30 icon per Batch Results row. Routable → a plain, clickable icon
 *  (the popup carries the status); unroutable → disabled, with the reason as the
 *  tooltip (never a dead link that opens an empty chart). */
export function TrendIcon({ route, onOpen }: { route?: TrendRoute; onOpen: () => void }) {
  const routable = !!route?.routable;
  const tip = routable ? `Trend — ${route!.chartLabel}` : route?.reason ?? 'No trend for this row';
  return (
    <button
      type="button"
      className="trend-ico"
      disabled={!routable}
      title={tip}
      aria-label={tip}
      onClick={routable ? onOpen : undefined}
    >
      <i className="ti ti-trending-up" aria-hidden />
    </button>
  );
}

// ── Popup data model ─────────────────────────────────────────────────────────

type PanelStatus = 'ok' | 'warn' | 'bad' | 'na';

interface QcPanel {
  kind: 'qc';
  label: string;
  pts: Pt[];
  st: Stat;
  field: Field;
  status: PanelStatus;
}
/** One horizontal reference line on a client/IS panel. */
interface RefLine {
  value: number;
  label: string;
  /** Stroke colour (a hex from the trend palette below). */
  color: string;
  /** SVG dash pattern; omit for a solid line (the mean). */
  dash?: string;
  /** Legend caption ("Mean", "Warning", "Permit") — a line without one is drawn
   *  but not listed (e.g. the lower warning shares the "Warning" legend entry). */
  legend?: string;
  /** Where the value label rides relative to the line (prototype §1). MCL sits just
   *  ABOVE its line at the right (insideTopRight); the Mean sits AT the line level at
   *  the right ('right'); a top-edge line (above-range MCL) labels just BELOW it
   *  (insideBottomRight) so it isn't clipped. */
  labelPos?: 'insideTopRight' | 'insideBottomRight' | 'right' | 'insideRight';
}
interface ClientPanel {
  kind: 'client';
  label: string;
  data: { label: string; v: number }[];
  units: string;
  status: PanelStatus;
  /** The reference lines drawn over the series. A site trend carries Mean,
   *  Warning (± 2 SD, so two lines sharing one legend entry) and Permit (client
   *  request 2026-09-03); an internal standard's recovery carries the nominal
   *  100% target only. Empty → a bare series. */
  lines: RefLine[];
  /** Y-axis ceiling that ALWAYS clears every reference line AND is a round number —
   *  a site far below its permit auto-scales to the data and would otherwise clip a
   *  line off-chart (hiding the drift the trend exists to show), and an un-rounded
   *  max renders an ugly float tick (0.20000000002). */
  yMax: number;
  /** Y-axis floor. Frames the chart around the DATA (variation visible) rather than
   *  forcing 0, so a result line far below a large limit is not flattened. */
  yMin?: number;
  /** Prototype-style summary line under the chart ("N approved results over the last
   *  12 months · MCL X · mean Y (informational)"). Omitted when there is no data. */
  caption?: string;
  /** §1c "No scope available": the client sample has NO Site AND NO Project, so there is
   *  no meaningful population to trend. The panel renders an honest empty state (no
   *  chart, no fetch) instead of a series. */
  noScope?: boolean;
  /** When the panel HAS a scope but still resolved to zero points, the backend's
   *  classified reason WHY (pending validation · all non-detects · no collect date ·
   *  out of window · none) — mapped to wording by `trendEmptyMessage`. Undefined when
   *  the diagnostic wasn't needed or failed (→ the generic message). */
  emptyReason?: string;
}
interface ErrPanel {
  kind: 'err';
  label: string;
  message: string;
}
/** A surrogate compound's recovery series — each result a point, coloured by the
 *  parent sample's category, drawn against the Low/High acceptance limits. */
interface SurrogatePanel {
  kind: 'surrogate';
  label: string;
  /** Which band chart this is — Surrogate recovery (v10 §3) or Internal Standard
   *  response (v10 §4). They share this panel + render but differ in the limit-line
   *  labels/style, the centre line, the legend text and the caption noun. */
  variant: 'surrogate' | 'is';
  /** One point per surrogate result: date label, recovery %, parent category. */
  data: { label: string; v: number; cat: string }[];
  /** Modal acceptance limits across the window (all points share the compound's
   *  configured limit); null → unconfigured, no line drawn. */
  low: number | null;
  high: number | null;
  status: PanelStatus;
  /** Distinct parent-sample categories present, for the coloured legend. */
  cats: string[];
  /** IS only (v10 §4): the dominant instrument the series ran on, "(+N more)" when it
   *  spans several. null for surrogate (or when no instrument is carried). */
  instrument: string | null;
  /** How many points fell outside the Low/High limits (drives the red caption note). */
  breached: number;
  /** Points within the limits but within 10% of the band of the low / high limit —
   *  the v10 §4 "approaching the limit" warning (amber caption note). */
  warnLow: number;
  warnHigh: number;
  yMin: number;
  yMax: number;
}
/** v10 §2 one-sided limit trend — each value plotted, judged against a configured
 *  limit as the PRIMARY line (a value above it is Out of control, red point). Serves
 *  both the §2 Blank Result Trend (limit = Reporting Limit, with an OPTIONAL statistical
 *  Mean/+2SD/+3SD band) and the §2b Duplicate RPD Trend (limit = RPD Control Limit, NO
 *  band — one-sided ≥ 0, closer to zero is better). */
interface LimitPanel {
  kind: 'limit';
  label: string;
  /** One point per value: date label, the TRUE value, whether it breached the limit,
   *  plus the value actually plotted — `vPlot` is `v` clamped into [yMin, yMax], and
   *  `clamped` marks a point that fell outside the framed band (Option A framing:
   *  the band sets the scale, an excursion is pinned to the edge and annotated). */
  data: { label: string; v: number; out: boolean; vPlot: number; clamped: boolean }[];
  /** How many points were pinned to the top / bottom edge (0 when everything fits). */
  clampedHigh: number;
  clampedLow: number;
  /** The configured UPPER limit (RL for a blank, RPD control for a dup, Recovery UCL). */
  rl: number | null;
  /** The configured LOWER limit — TWO-SIDED (§2c Recovery LCL); null for a one-sided
   *  Blank / RPD (a value can only breach upward). */
  lcl: number | null;
  /** Optional target line (§2c Recovery: 100%, grey dashed); null otherwise. */
  target: number | null;
  /** On-chart label for the UPPER line ("Blank Acceptance Limit" / "RPD Control Limit" /
   *  "Recovery UCL"). */
  limitLabel: string;
  /** On-chart label for the LOWER line (§2c "Recovery LCL"); '' when one-sided. */
  limitLabelLow: string;
  /** Legend text for the limit line(s) ("Recovery LCL / UCL" / "… (primary)"). */
  limitLegend: string;
  /** Solid red limit lines (§2c Recovery) vs dashed (Blank / RPD primary). */
  solidLimit: boolean;
  /** Legend label for the plotted value ("Blank result" / "RPD" / "Recovery"). */
  pointLabel: string;
  /** Legend label for a breach ("Out of control" / "Out of control (> limit)"). */
  outLabel: string;
  /** Whether to draw the OPTIONAL statistical Mean/+2SD/+3SD band (Blank: yes; else no). */
  showBand: boolean;
  mean: number | null;
  plus2: number | null;
  plus3: number | null;
  units: string;
  status: PanelStatus;
  yMin: number;
  yMax: number;
  /** How many values breached the limit. */
  breached: number;
  /** Prototype §2 summary line, split so the out-of-control clause renders RED
   *  (matches the mock): base (grey) · alert (red, the breach) · tail (grey). */
  captionBase: string;
  captionAlert: string;
  captionTail: string;
}
type PanelData = QcPanel | ClientPanel | SurrogatePanel | LimitPanel | ErrPanel;

interface ApiTrendPoint {
  siteName: string | null;
  /** Project.ProjectId — the grouping key on a Project-trend point (v10 §1b);
   *  null on a Site-trend point. */
  projectName: string | null;
  parameter: string | null;
  month: string | null;
  collectDate: string | null;
  value: number | null;
  units: string | null;
  mcl: number | null;
}

interface ApiSurrogatePoint {
  enteredDate: string | null;
  recovery: number | null;
  category: string | null;
  lowLimit: number | null;
  highLimit: number | null;
}

interface ApiIsRespPoint {
  enteredDate: string | null;
  responsePct: number | null;
  category: string | null;
  instrument: string | null;
  lowLimit: number | null;
  highLimit: number | null;
}

/** A one-sentence rationale per route — the "why this chart applies" the guide
 *  wants beside the derivation chain. */
const WHY: Record<string, string> = {
  'Blank Result Trend':
    'Trends the measured blank result to identify contamination, carryover, or rising background. Each blank is judged against the method-blank acceptance criterion (the primary line = the Reporting Limit) — a blank above it is out of control. The statistical Mean / ±2SD / ±3SD are shown only as an optional, informational band; they do not define pass/fail unless the method requires it.',
  RPD: 'A duplicate is of a sample that changes every batch, so the raw value is noise. RPD normalises concentration out and leaves the precision the lab controls.',
  'QC Recovery Trend':
    'Recovery answers accuracy — how close the measured spike is to what was added, against the configured Recovery Lower/Upper Control Limits, with the 100% target shown for reference.',
  'Sample Result Trend':
    'A client sample is not QC — this trends the location’s approved history for this analyte against its regulatory limit (MCL). The mean is a thin informational line showing whether the current result sits above or below the site’s usual level, and never drives Warning / Out-of-control.',
  'Internal Standard Response Trend':
    'An internal standard (e.g. Yttrium on an ICP run) rides on every sample but is not a reported analyte — this is its Response % (each response ÷ the batch-average response × 100, so 100% = the reference) across the lab’s recent runs against the method’s acceptance limits, each point coloured by the sample it rode on.',
  'Surrogate Recovery Trend':
    'A surrogate is spiked into every sample to check extraction/analysis recovery, but is not a reported analyte and carries no QC type — this is its recovery across the lab’s recent runs against the compound’s Low/High acceptance limits, each point coloured by the sample it rode on.',
};

/** Root-role BUCKET of a raw QC-type name (Bin Yu v10's four categories + Duplicate),
 *  so a point's colour and the legend read as a handful of roles rather than every
 *  raw QC-type name (MS, CCB, ICV, LCSD Digested, LOQ Digested …). Mirrors the
 *  router's classifyByTypeName keyword ladder; unknown → Other. */
function catBucket(cat: string): string {
  const c = (cat ?? '').trim().toUpperCase();
  if (!c) return 'Other';
  // Blank first — "CCB"/"MB" carry no "BLANK" in the string, so match the prefixes
  // too (mirrors the router's classifyByTypeName). Trip/RF/Method Blank carry it.
  if (/BLANK/.test(c) || /^CCB/.test(c) || /^MB/.test(c)) return 'Blank';
  if (/DUP/.test(c)) return 'Duplicate';
  if (/^LCS/.test(c) || /^MS/.test(c) || /SPIKE/.test(c)) return 'Spike'; // LCS(D) / MS(D)
  if (/CCV/.test(c) || /^ICV/.test(c) || /LOQ/.test(c) || /STANDARD/.test(c) || /^CAL/.test(c)) return 'Standard';
  if (c === 'SAMPLE') return 'Sample';
  return 'Other';
}

/** v10 category palette — matches the prototype's `--cat-*` tokens so a reviewer
 *  reads the AI chart and the design mock with one colour language. */
const CAT_COLORS: Record<string, string> = {
  Sample: '#3a7bd5',    // blue
  Blank: '#2a9d8f',     // teal
  Standard: '#7b52ab',  // purple
  Spike: '#e8670a',     // orange
  Duplicate: '#d97706', // amber
  Other: '#6b7280',     // grey
};

/** Point colour by the parent sample's root category — so a reviewer sees at a
 *  glance whether a low recovery/response clusters in one sample type (a matrix
 *  effect) or is method-wide. Accepts a raw category or an already-bucketed one. */
function catColor(cat: string): string {
  return CAT_COLORS[catBucket(cat)] ?? '#6b7280';
}

/** The most-common non-null value — the configured limit shared by the window's
 *  points (mirrors the Phase-2 modal applied-limit rule; a stray row's odd limit
 *  cannot move the reference line). */
function modalNum(vals: (number | null | undefined)[]): number | null {
  const counts = new Map<number, number>();
  for (const v of vals) {
    if (v == null) continue;
    const n = Number(v);
    if (Number.isFinite(n)) counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestC = 0;
  for (const [n, c] of counts) if (c > bestC) { best = n; bestC = c; }
  return best;
}

/** Frame a recovery axis around the data AND the acceptance limits with padding,
 *  rounded to a clean 10 — so the Low/High lines and every point are always on
 *  chart (a run sitting inside 70–130 must not clip the limit lines off-screen). */
function frameAxis(vals: number[], lo: number | null, hi: number | null): { min: number; max: number } {
  const all = [...vals];
  if (lo != null) all.push(lo);
  if (hi != null) all.push(hi);
  if (!all.length) return { min: 0, max: 120 };
  const mn = Math.min(...all);
  const mx = Math.max(...all);
  const pad = Math.max(5, (mx - mn) * 0.1);
  return { min: Math.max(0, Math.floor((mn - pad) / 10) * 10), max: Math.ceil((mx + pad) / 10) * 10 };
}

/** Round a value UP to two significant figures, so the Y-axis ceiling is a clean
 *  number (1.1, 0.34) rather than an un-rounded float that Recharts renders with
 *  floating-point noise (0.20000000002). */
function niceCeil(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  return (Math.ceil((v / mag) * 10) / 10) * mag;
}

/** Reference-line palette for the client/IS panels — the mean is a neutral grey
 *  centre line, the ± 2 SD warning band amber, the permit/limit red. Matched to
 *  the Control Charts screen so a reviewer reads both with one colour language. */
const LINE = {
  mean: '#6b7280',    // neutral grey — centre of the history
  warning: '#d97706', // amber — ± 2 SD early-warning band
  permit: '#d92d20',  // red — the compliance limit (permit / MCL)
} as const;

function synthStat(parameter: string, count: number): Stat {
  return { parameter, kind: 'standard', mean: null, std: null, ucl: null, uwl: null, lwl: null, lcl: null, count };
}

function panelStatusOf(pts: Pt[], st: Stat): PanelStatus {
  if (!pts.length) return 'na';
  // Attribute charts — sterility (`attribute`) and Presence/Absence (`presence`) —
  // are pass/fail: they carry no mean / ± SD band and no control limit, and a single
  // point is meaningful, so the chemistry guards below (≥ 2 points AND an upper limit)
  // would wrongly pin them to "Baseline". Any excursion is out of control — a blank
  // that grew, or a positive control that was absent. Read the server-side
  // `outOfControl` count so the pill matches the QA/QC Control Charts screen exactly
  // (standing rule 8 — ONE definition of out-of-control, computed once in the API).
  if (st.kind === 'attribute' || st.kind === 'presence') {
    return (st.outOfControl ?? 0) > 0 ? 'bad' : 'ok';
  }
  if (st.count < 2 || st.ucl == null) return 'na';
  const out = pts.some((p) => (st.ucl != null && p.v > st.ucl) || (st.lcl != null && p.v < st.lcl));
  if (out) return 'bad';
  const warn = pts.some((p) => (st.uwl != null && p.v > st.uwl) || (st.lwl != null && p.v < st.lwl));
  return warn ? 'warn' : 'ok';
}

/** The heading for a micro QC panel, from the RESOLVED chart field the shared series
 *  engine picked (a qualitative Presence/Absence test auto-charts `pa`/`growth`
 *  regardless of the router's quantitative hint), so the panel title always matches
 *  the chart drawn. Returns '' for a non-micro field so the caller keeps the route's
 *  own label. */
function microPanelLabel(field: Field): string {
  switch (field) {
    case 'growth': return 'Sterility (SM 9020 B)';
    case 'pa':     return 'Positive control (Presence/Absence)';
    case 'rng':    return 'Duplicate range (SM 9020 B)';
    case 'logc':   return 'Positive control (log₁₀)';
    default:       return '';
  }
}

const STATUS_LABEL: Record<PanelStatus, string> = {
  ok: 'In control',
  warn: 'Watch',
  bad: 'Out of control',
  na: 'Baseline',
};

// ── Panel loaders ────────────────────────────────────────────────────────────

async function loadQcPanel(route: TrendRoute, panel: TrendPanel, signal: AbortSignal): Promise<PanelData> {
  const parameter = route.parameter ?? '';
  const scope: ApiScope = {
    testMethodId: route.testMethodOid ?? '',
    from: null,
    // Anchor the window to the row the popup was opened from (user report
    // 2026-09-28). The series endpoint already honours `to`
    // (ControlChartSql.ScopeFilter: AnalyzedDate < DATEADD(day, 1, @To)), so the
    // newest-30 trim now yields the 30 points ENDING at this row instead of the
    // 30 most recent in the lab. Null (a surface with no date) is unchanged.
    // Normalised to ISO — a display-formatted anchor (SDMS grid) otherwise fails
    // the DateTime bind → 400 (anchorToIso above).
    to: anchorToIso(route.anchorDate),
    instruments: [],
    analysts: [],
    role: null,
    qcTypes: panel.qcTypes ?? [],
    parameters: parameter ? [parameter] : [],
    field: panel.field ?? null,
    // Micro (SM 9020 B): tell the series endpoint to parse the MPN/CFU count from
    // Result and apply the micro statistics (range / individuals-log / sterility).
    mode: panel.micro ? 'micro' : null,
  };
  const result = await fetchSeries(scope, signal);
  const field = (result.field ?? panel.field ?? 'rec') as Field;
  const pts: Pt[] = result.rows
    .filter((r) => !parameter || r.p === parameter)
    .map((r) => ({ v: valueOf(r, field), d: r.d }))
    .filter((p): p is Pt => typeof p.v === 'number' && Number.isFinite(p.v));
  const st = result.stats.find((s) => s.parameter === parameter) ?? result.stats[0] ?? synthStat(parameter, pts.length);

  // v10 §2 config-limit trends: Blank Result ('res', one-sided upper + optional stat
  // band), Duplicate RPD ('rpd', one-sided upper, no band) and QC Recovery ('rec',
  // TWO-SIDED LCL/UCL + optional 100% target). ApplyConfigLimits already set st.ucl/lcl
  // = the configured limits and kept the statistical mean/std — no backend change.
  if (field === 'res' || field === 'rpd' || field === 'rec') {
    const isBlank = field === 'res';
    const isRec = field === 'rec';
    const ucl = st.ucl;                 // upper limit
    const lcl = isRec ? st.lcl : null;  // lower limit — Recovery only (Blank/RPD one-sided)
    const target = isRec ? 100 : null;  // 100% target — Recovery only
    const mean = st.mean;
    const sd = st.std;
    const plus2 = mean != null && sd != null ? mean + 2 * sd : null;
    const plus3 = mean != null && sd != null ? mean + 3 * sd : null;
    const data = pts.map((p) => ({
      label: fmtLocalDate(p.d),
      v: p.v,
      out: (ucl != null && p.v > ucl) || (lcl != null && p.v < lcl),
    }));
    const units = isBlank ? (result.rows.find((r) => r.unit)?.unit ?? '') : '%';
    const breached = data.filter((d) => d.out).length;
    const configured = ucl != null || lcl != null;
    const status: PanelStatus = !configured ? 'na' : breached > 0 ? 'bad' : 'ok';

    // Frame: Recovery sits near 100, so frame around [LCL, data, UCL, target]; one-sided
    // Blank/RPD frame up from 0 past the highest of data / limit / (+3SD when banded).
    // FRAMING — Option A (owner decision 2026-09-28, "the control charts are not
    // presented properly"). The frame is built from the CONTROL BAND, never from the
    // worst point.
    //
    // It used to be hi = max(data, LCL, UCL, target), so a single excursion owned the
    // ceiling: live QC recoveries reach 355% against 80-120 limits, which squeezed the
    // acceptance band to ~13% of the plot height and collapsed the other ~29 points and
    // all three reference lines into a few pixels. That is backwards for a control
    // chart — the worse the excursion, the less readable the limits became, and the
    // limits are the whole point of the chart.
    //
    // Now: frame the band (+ ordinary scatter just outside it, so a slightly-out run
    // still reads truthfully), and CLAMP anything beyond to the frame edge, where it is
    // drawn as a red arrow carrying its real value. Standard control-chart practice.
    let yMin: number, yMax: number;
    const ADMIT = 0.3;   // admit scatter within 30% of the band height beyond an edge
    const PAD = 0.15;    // breathing room, as a fraction of the band height
    if (isRec) {
      const band = [lcl, ucl, target].filter((v): v is number => v != null);
      if (band.length >= 2) {
        const bLo = Math.min(...band);
        const bHi = Math.max(...band);
        const bandH = Math.max(bHi - bLo, 1);
        const near = data
          .map((d) => d.v)
          .filter((v) => v >= bLo - bandH * ADMIT && v <= bHi + bandH * ADMIT);
        const lo = near.length ? Math.min(bLo, ...near) : bLo;
        const hi = near.length ? Math.max(bHi, ...near) : bHi;
        const pad = Math.max(2, bandH * PAD);
        yMin = Math.max(0, lo - pad);
        yMax = hi + pad;
      } else {
        // No configured band — nothing to clamp to, so keep the honest data frame.
        const vals = [...data.map((d) => d.v), ...band];
        const lo = vals.length ? Math.min(...vals) : 0;
        const hi = vals.length ? Math.max(...vals) : 120;
        const pad = Math.max(2, (hi - lo) * 0.1);
        yMin = Math.max(0, lo - pad);
        yMax = hi + pad;
      }
    } else {
      // One-sided (Blank result / Duplicate RPD): same disease, same cure — frame up
      // from 0 past the acceptance line (and the +3SD band when shown), admitting
      // scatter near it; a runaway blank is clamped rather than flattening the rest.
      const ref = Math.max(ucl ?? 0, isBlank && plus3 != null ? plus3 : 0);
      if (ref > 0) {
        const near = data.map((d) => d.v).filter((v) => v <= ref * (1 + ADMIT));
        const hi = near.length ? Math.max(ref, ...near) : ref;
        yMin = 0;
        yMax = hi * 1.12;
      } else {
        const top = data.length ? Math.max(...data.map((d) => d.v)) : 0;
        yMin = 0;
        yMax = top > 0 ? top * 1.12 : 1;
      }
    }

    // Plot the CLAMPED value; keep the true one on `v` for the tooltip, the caption
    // and the out-of-limits test, so nothing downstream reads a clipped number.
    const plotted = data.map((d) => ({
      ...d,
      vPlot: Math.min(Math.max(d.v, yMin), yMax),
      clamped: d.v > yMax || d.v < yMin,
    }));
    const clampedHigh = plotted.filter((d) => d.clamped && d.v > yMax).length;
    const clampedLow = plotted.filter((d) => d.clamped && d.v < yMin).length;

    const uSuffix = units ? ` ${units}` : '';
    // Caption in three parts so the out-of-control clause renders RED (§2 mock).
    const captionBase = isBlank
      ? `n = ${data.length}` +
        (ucl != null ? ` · acceptance limit ${trimZeros(ucl)}${uSuffix} (primary)` : '') +
        (breached > 0 ? '' : ucl != null ? ' — none breached.' : '.')
      : isRec
        ? `n = ${data.length}` +
          (ucl != null && lcl != null ? ` · configured recovery limits ${trimZeros(lcl)}–${trimZeros(ucl)}%` : '') +
          (target != null ? ` · 100% target` : '') +
          (breached > 0 ? '' : '.')
        : `n = ${data.length}` +
          (ucl != null ? ` · acceptance ≤ ${trimZeros(ucl)}% (one-sided, no lower limit)` : '') +
          (breached > 0 ? '' : ucl != null ? ' · no exceedances.' : '.');
    const captionAlert =
      breached === 0
        ? ''
        : isBlank
          ? ` — ${breached} recent blank${breached === 1 ? '' : 's'} breached it (out of control).`
          : isRec
            ? ` · ${breached} result${breached === 1 ? '' : 's'} outside the limits — out of control.`
            : ` · ${breached} duplicate${breached === 1 ? '' : 's'} exceeded ${ucl != null ? `${trimZeros(ucl)}%` : 'the limit'} — out of control.`;
    const captionTail =
      isBlank && mean != null && sd != null
        ? ` Mean ${trimZeros(mean)} · SD ${trimZeros(sd)} shown as an optional band only.`
        : '';

    return {
      kind: 'limit',
      label: panel.label,
      data: plotted,
      clampedHigh,
      clampedLow,
      rl: ucl,
      lcl,
      target,
      limitLabel: isBlank ? 'Blank Acceptance Limit' : isRec ? 'Recovery UCL' : 'RPD Control Limit',
      limitLabelLow: isRec ? 'Recovery LCL' : '',
      limitLegend: isRec ? 'Recovery LCL / UCL' : isBlank ? 'Blank Acceptance Limit (primary)' : 'RPD Control Limit (primary)',
      solidLimit: isRec,
      pointLabel: panel.pointLabel ?? (isBlank ? 'Blank result' : isRec ? 'Recovery' : 'RPD'),
      outLabel: isBlank ? 'Out of control' : isRec ? 'Out of control' : 'Out of control (> limit)',
      showBand: isBlank,
      mean, plus2, plus3, units,
      status, yMin, yMax, breached, captionBase, captionAlert, captionTail,
    };
  }

  // Micro: title the panel from the RESOLVED field, not the router's quantitative
  // hint — a Presence/Absence test the engine charted as `pa` must read
  // "Positive control (Presence/Absence)", not "Positive control (log₁₀)".
  const label = panel.micro ? (microPanelLabel(field) || panel.label) : panel.label;
  return { kind: 'qc', label, pts, st, field, status: panelStatusOf(pts, st) };
}

/** Shared loader for the limit-band recovery charts — Surrogate recovery (v10 §3)
 *  and Internal Standard response (v10 §4). Both plot ONE value per event, scoped
 *  by method × compound, against modal Low/High acceptance-limit lines + a 100%
 *  centre, with each marker coloured by the parent sample's QC category. `pick`
 *  selects the charted value from the endpoint's point shape; the two differ only
 *  in the endpoint + that field, so they share this body (rule 8 — don't reinvent).
 *  Returns a `surrogate`-kind panel so both use the one render path below. */
async function loadLimitBandPanel<
  T extends { enteredDate: string | null; category: string | null; lowLimit: number | null; highLimit: number | null; instrument?: string | null },
>(
  route: TrendRoute,
  panel: TrendPanel,
  endpoint: string,
  pick: (p: T) => number | null,
  variant: 'surrogate' | 'is',
  signal: AbortSignal,
): Promise<PanelData> {
  const methodOid = route.testMethodOid ?? '';
  const parameterOid = route.parameterOid ?? '';
  if (!methodOid || !parameterOid) {
    return { kind: 'err', label: panel.label, message: `${panel.label} needs a method and compound to trend.` };
  }
  const res = await api.get<{ success: boolean; data: T[] }>(endpoint, {
    params: { methodOid, parameterOid, months: 12 },
    signal,
  });
  const raw = (res.data?.data ?? []).filter((p) => {
    const v = pick(p);
    return v != null && Number.isFinite(Number(v));
  });
  const data = raw.map((p) => ({
    label: p.enteredDate ? fmtLocalDate(p.enteredDate) : '',
    v: Number(pick(p)),
    cat: catBucket((p.category ?? '').trim()),
  }));
  const low = modalNum(raw.map((p) => p.lowLimit));
  const high = modalNum(raw.map((p) => p.highLimit));

  // Out of control = any point outside the limits. Warning ("approaching the limit",
  // v10 §4) = a point that stays WITHIN the limits but comes within 10% of the band of
  // a limit — counted across the whole series (not just the latest), so a mid-series
  // dip is flagged like the mock.
  const breached = data.filter((d) => (high != null && d.v > high) || (low != null && d.v < low)).length;
  const band = low != null && high != null ? high - low : null;
  const wm = band != null ? band * 0.1 : null;
  const warnLow = low != null && wm != null ? data.filter((d) => d.v >= low && d.v < low + wm).length : 0;
  const warnHigh = high != null && wm != null ? data.filter((d) => d.v <= high && d.v > high - wm).length : 0;

  // Out of control (any breach) > Warning (any approach) > In control; no limit → Baseline.
  const status: PanelStatus =
    low == null && high == null
      ? 'na'
      : breached > 0
        ? 'bad'
        : warnLow + warnHigh > 0
          ? 'warn'
          : 'ok';

  // Legend category order fixed to the v10 mock (Blank · Standard · Spike · Sample,
  // then Duplicate · Other) rather than order-of-appearance, so the swatches read the
  // same on every chart.
  const CAT_ORDER = ['Blank', 'Standard', 'Spike', 'Sample', 'Duplicate', 'Other'];
  const catRank = (c: string) => { const i = CAT_ORDER.indexOf(c); return i === -1 ? CAT_ORDER.length : i; };
  const cats = [...new Set(data.map((d) => d.cat))].sort((a, b) => catRank(a) - catRank(b) || a.localeCompare(b));

  // IS (§4) header context: the instrument(s) the series ran on. The series spans
  // method × compound and can cross instruments, so show the DOMINANT one, with
  // "(+N more)" when several. Surrogate points carry no instrument → null.
  const instCounts = new Map<string, number>();
  for (const p of raw) {
    const inst = (p.instrument ?? '').trim();
    if (inst) instCounts.set(inst, (instCounts.get(inst) ?? 0) + 1);
  }
  let instrument: string | null = null;
  if (instCounts.size) {
    const sorted = [...instCounts.entries()].sort((a, b) => b[1] - a[1]);
    instrument = sorted[0]![0] + (sorted.length > 1 ? ` (+${sorted.length - 1} more)` : '');
  }
  const { min, max } = frameAxis(data.map((d) => d.v), low, high);
  return { kind: 'surrogate', variant, label: panel.label, data, low, high, status, cats, instrument, breached, warnLow, warnHigh, yMin: min, yMax: max };
}

const loadSurrogatePanel = (route: TrendRoute, panel: TrendPanel, signal: AbortSignal) =>
  loadLimitBandPanel<ApiSurrogatePoint>(
    route, panel, '/api/v1/analysis/result-validation/surrogate-trend', (p) => p.recovery, 'surrogate', signal,
  );

const loadIsRespPanel = (route: TrendRoute, panel: TrendPanel, signal: AbortSignal) =>
  loadLimitBandPanel<ApiIsRespPoint>(
    route, panel, '/api/v1/analysis/result-validation/is-response-trend', (p) => p.responsePct, 'is', signal,
  );

/** When a scoped client trend resolves to zero points, ask the backend WHY (pending
 *  validation · all non-detects · no collect date · out of window · none) so the popup
 *  shows a specific reason. Called only on the empty path, so the common (non-empty)
 *  case pays nothing. A failed/aborted diagnostic returns undefined → the generic
 *  message (§18.15 C6 is a C# catch rule; here best-effort fallback is correct). */
async function fetchTrendEmptyReason(
  batchOid: string,
  scope: { site?: string | null; project?: string | null; analyte: string },
  signal: AbortSignal,
): Promise<string | undefined> {
  try {
    const res = await api.get<{ success: boolean; data: { reason: string; months: number } }>(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/trend/empty-reason`,
      {
        params: {
          analyte: scope.analyte,
          site: scope.site ?? undefined,
          project: scope.project ?? undefined,
          months: 12,
        },
        signal,
      },
    );
    return res.data?.data?.reason;
  } catch {
    return undefined;
  }
}

async function loadTrendPanel(
  route: TrendRoute,
  panel: TrendPanel,
  batchOid: string,
  mode: 'client' | 'is',
  // Legacy permit/warn limits from the Batch Results row — NO LONGER DRAWN (v10 §1
  // replaced the Permit + Warning lines with MCL + informational Mean, 2026-09-08).
  // Kept in the signature (prefixed unused) so the caller's prop threading is
  // unchanged; remove with the permitLimit/permitWarn props in a later cleanup.
  _rowLimits: { permitLimit: string | null; permitWarn: string | null },
  signal: AbortSignal,
): Promise<PanelData> {
  // §1c "No scope available": neither Site nor Project on a client panel → the honest
  // empty state, no fetch (there is no population to trend).
  if (mode === 'client' && !panel.site && !panel.project) {
    return {
      kind: 'client', label: panel.label, data: [], units: '', status: 'na', lines: [], yMax: 1,
      noScope: true,
    };
  }

  // Site → Project fallback (v10 §1b): a panel carries EITHER a site or a project.
  // A project-grouped panel calls the project-scoped endpoint and matches on
  // ProjectName; everything downstream (MCL, mean, framing, caption) is identical.
  const project = (panel.project ?? '').trim().toLowerCase();
  const isProject = project !== '';
  const endpoint = isProject
    ? `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/trend/project`
    : `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/trend`;
  const res = await api.get<{ success: boolean; data: ApiTrendPoint[] }>(endpoint, {
    params: { months: 12 },
    signal,
  });
  const group = isProject ? project : (panel.site ?? '').trim().toLowerCase();
  const analyte = (route.parameter ?? '').trim().toLowerCase();
  // EXACT group match (case-insensitive, trimmed). Both the Batch Results grid and
  // the trend reader source SiteName / ProjectName from the SAME joins, so the
  // strings are identical — and a substring match is actively WRONG here: a short
  // code like "D1" is contained in "P2Ph1NED1" ("…NE[D1]"), so substring matching
  // mixed site D1's history into P2Ph1NED1's chart (verified 2026-09-02).
  const groupOf = (p: ApiTrendPoint) => (isProject ? p.projectName : p.siteName);
  const groupMatch = (p: ApiTrendPoint) => (groupOf(p) ?? '').trim().toLowerCase() === group;
  const raw = (res.data?.data ?? []).filter(
    (p) => p.value !== null && p.month && groupMatch(p) && (p.parameter ?? '').trim().toLowerCase() === analyte,
  );
  // Latest value per month, ordered oldest → newest.
  // Plot EVERY approved result, oldest → newest (owner decision 2026-09-30). This
  // used to collapse to the LATEST value per month, which hid same-month siblings:
  // a PT-Study batch of 5 same-day approved samples showed a SINGLE point (192.6)
  // and an inaccurate "1 result / mean 192.6", instead of all 5 (true mean 197.0).
  // The common case — a site sampled ~monthly — is unchanged (one result per month
  // still yields one point per month); only months with multiple results now show
  // all of them, and the count / mean reflect the real data.
  const ordered = [...raw].sort((a, b) =>
    (a.collectDate ?? a.month!).localeCompare(b.collectDate ?? b.month!));
  // Display cap: show only the NEWEST N points — the SAME "last 30 is enough" rule
  // QC / Surrogate / IS already use (owner 2026-10-03), now extended to the Sample
  // Result Trend for readability (an 89-point site crowded the x-axis into an
  // unreadable band). The oldest beyond the cap are dropped; same-period siblings
  // within the newest N are kept (the 2026-09-30 "plot every approved result" fix
  // still holds for what's shown). The caption states the full window count.
  const totalApproved = ordered.length;
  const shown =
    totalApproved > SAMPLE_TREND_MAX_POINTS ? ordered.slice(-SAMPLE_TREND_MAX_POINTS) : ordered;
  const data = shown.map((p) => ({
    label: p.collectDate ? fmtLocalDate(p.collectDate) : p.month!,
    v: Number(p.value),
  }));
  const units = raw.find((p) => p.units)?.units ?? '';
  const latest = data.length ? data[data.length - 1]!.v : null;
  const dataMax = data.length ? Math.max(...data.map((d) => d.v)) : 0;

  if (mode === 'is') {
    // IS recovery: the ratio against the nominal 100% (1.0). Judged by how far
    // the latest recovery strays from unity — a universal sanity band, not a
    // lab-specific compliance limit (which the config would supply).
    const ref = 1;
    const dev = latest == null ? null : Math.abs(latest - 1);
    const status: PanelStatus = dev == null ? 'na' : dev <= 0.2 ? 'ok' : dev <= 0.4 ? 'warn' : 'bad';
    return {
      kind: 'client',
      label: panel.label,
      data,
      // A recovery ratio is dimensionless; the source unit ("Ratio"/mislabeled
      // "mg/L") is meaningless here, so it is dropped from the axis + tooltip.
      units: '',
      status,
      lines: [{ value: ref, label: 'Target 100%', color: LINE.permit, dash: '6 4', legend: 'Target 100%' }],
      yMax: niceCeil(Math.max(dataMax, ref) * 1.12),
    };
  }

  // ── Sample Result Trend reference lines (v10 §1, 2026-09-08) ──────────────
  // Two marks over the location's history, matched to the prototype:
  //   • MCL   — the applicable regulatory limit for this analyte = Testparameter.MCL
  //             (returned per point by the trend endpoint). No MCL → no red line.
  //   • Mean  — the arithmetic centre of the plotted history, a THIN INFORMATIONAL
  //             line (is the current result above/below the site's usual level?). It
  //             NEVER drives the status. (Superseded the 2026-09-03 Permit + Warning
  //             lines — owner-approved 2026-09-08 to match the v10 §1 mock, which the
  //             `rowLimits` permit/warn props now no longer feed.)
  const uSuffix = units ? ` ${units}` : '';
  const mclVal = modalNum(raw.map((p) => p.mcl));
  const meanVal = mean(data.map((d) => d.v));

  // Frame the y-axis around the DATA + Mean so the result variation is VISIBLE (the
  // point of §1 — is the current result above/below the site's usual level?). Include
  // the MCL in the frame ONLY when the data is within reach of it (≤ 2× the data max):
  // a far-above MCL (e.g. Sulfate ~5 vs 250) would flatten the line into the axis
  // floor, so it stays in the caption instead of squashing the chart. When the MCL is
  // out of frame its reference line is not drawn (Recharts clips it) — the caption
  // still states the number.
  const dMin = data.length ? Math.min(...data.map((d) => d.v)) : 0;
  // In frame UNLESS the MCL is far ABOVE the data (that would squash the result line
  // into the floor → the "▲ above range" marker instead). A MCL BELOW the data stays
  // IN frame: the result sitting above it IS the story (an exceedance), framing 0→data
  // shows it honestly, and the legend reads just "MCL". (Bug fix 2026-09-09: a low MCL
  // far below the data — e.g. Manganese 0.05 ppm vs a 10 ppm PT result — was wrongly
  // pinned to the TOP edge as "MCL ▲ above range"; the MCL is BELOW the data, not above.)
  const mclInFrame = mclVal != null && mclVal <= dataMax * 2;
  const framed: number[] = [...data.map((d) => d.v)];
  if (meanVal != null) framed.push(meanVal);
  if (mclInFrame) framed.push(mclVal!);
  const lo0 = framed.length ? Math.min(...framed) : 0;
  const hi0 = framed.length ? Math.max(...framed) : 1;
  const range = hi0 - lo0 || Math.abs(hi0) || 1;
  const pad = range * 0.12;
  const yMin = dMin >= 0 ? Math.max(0, lo0 - pad) : lo0 - pad;
  const yMax = hi0 + pad;

  const lines: RefLine[] = [];
  if (mclVal != null && mclInFrame) {
    lines.push({ value: mclVal, label: `MCL ${trimZeros(mclVal)}${uSuffix}`, color: LINE.permit, dash: '6 4', legend: 'MCL', labelPos: 'insideTopRight' });
  } else if (mclVal != null) {
    // MCL far above the framed data — pin an "above range" marker at the TOP EDGE so
    // the regulatory limit is always indicated (its true value in the label) without
    // flattening the result line (option B, 2026-09-08). Dotted to read as off-scale.
    // Its label sits just BELOW the top-edge line (insideBottomRight) so it isn't clipped.
    lines.push({
      value: yMax,
      label: `MCL ${trimZeros(mclVal)}${uSuffix} ▲`,
      color: LINE.permit,
      dash: '2 3',
      legend: 'MCL (above range)',
      labelPos: 'insideBottomRight',
    });
  }
  if (meanVal != null) {
    // Mean label sits at the line's RIGHT END, in the margin at the line's level — so
    // the dashed line stops before it and never crosses the text (owner: with
    // insideRight the line was running THROUGH the label). The wider right margin
    // (below) keeps it from clipping.
    lines.push({ value: meanVal, label: `Mean ${trimZeros(meanVal)}`, color: LINE.mean, dash: '5 4', legend: 'Mean (informational)', labelPos: 'right' });
  }

  // Informational history: only a result AT/OVER the regulatory MCL is Out of
  // control; the mean never drives status. No MCL → Baseline (nothing to judge).
  const status: PanelStatus =
    mclVal == null || latest == null ? 'na' : latest >= mclVal ? 'bad' : 'na';

  // Prototype summary line (always states the MCL, even when its line is out of frame).
  const caption =
    data.length > 0
      ? sampleTrendCountPhrase(totalApproved, SAMPLE_TREND_MAX_POINTS) +
        (mclVal != null ? ` · MCL ${trimZeros(mclVal)}${uSuffix}` : '') +
        (meanVal != null ? ` · mean ${trimZeros(meanVal)} (of the displayed results, informational)` : '')
      : undefined;

  // Empty with a real scope → ask the backend WHY, so the popup can say "all
  // non-detects" / "pending validation" / "no collect date" / "out of window"
  // instead of a blanket "no reviewed history". Only fires on the empty path.
  const emptyReason =
    data.length === 0
      ? await fetchTrendEmptyReason(
          batchOid,
          { site: panel.site, project: panel.project, analyte: route.parameter ?? '' },
          signal,
        )
      : undefined;

  return {
    kind: 'client',
    label: panel.label,
    data,
    units,
    status,
    lines,
    caption,
    yMin,
    yMax,
    emptyReason,
  };
}

// ── The popup ────────────────────────────────────────────────────────────────

/** Route mode — a resolved {@link TrendRoute} → QC / client / surrogate / IS panels
 *  (the SDMS Result Validation path). */
interface RoutePopupProps {
  route: TrendRoute;
  batchOid: string;
  sampleLabel: string;
  /** The batch's method (e.g. "EPA 300.0") — shown in the Sample Result Trend header
   *  (a client sample carries no QC classification, so it shows Site · Method ·
   *  Parameter instead, v10 §1). */
  method?: string | null;
  /** The opened row's assigned permit limit = Permitsetup Limit1 (grid Permit
   *  Limit) — drives the site-trend Permit line. Null → no permit line. */
  permitLimit?: string | null;
  /** The opened row's permit warning threshold = Permitsetup Limit3 ("Limit3
   *  (warn)") — drives the site-trend Warning line. Null → no warning line. */
  permitWarn?: string | null;
  onClose: () => void;
}

/** The props of the ONE shared Trend popup — a discriminated union over its two
 *  modes. Presence of `siteTrend` selects SITE mode (delegates to
 *  {@link SiteTrendDialog}); otherwise ROUTE mode. */
export type TrendChartPopupProps =
  | (RoutePopupProps & { siteTrend?: undefined })
  | {
      /** Site-trend config (everything {@link SiteTrendDialog} needs except its
       *  own open/onClose, which the popup threads through). Turning ON the
       *  optional site features — param/site pickers, nitrification watch, stats,
       *  CSV export, monthly table — is inherent to this mode. */
      siteTrend: Omit<SiteTrendProps, 'open' | 'onClose'>;
      open: boolean;
      onClose: () => void;
    };

/**
 * The ONE shared Trend popup every form uses (standing rule 8). Two modes:
 *  · ROUTE mode — pass a resolved `route` (+ `batchOid`): renders the QC /
 *    client Sample Result Trend / surrogate / IS panels. Used by SDMS Result
 *    Validation.
 *  · SITE mode — pass a `siteTrend` config (+ `open`): delegates to
 *    {@link SiteTrendDialog} — the station-history chart with the optional
 *    parameter/site pickers, nitrification-watch overlay, stats grid, CSV export
 *    and monthly-values table. Used by Field Data (Entry / Validation / Tracking)
 *    and any other site-keyed form.
 */
export function TrendChartPopup(props: TrendChartPopupProps) {
  if ('siteTrend' in props && props.siteTrend) {
    return <SiteTrendDialog open={props.open} onClose={props.onClose} {...props.siteTrend} />;
  }
  return <RouteTrendPopup {...props} />;
}

function RouteTrendPopup({
  route,
  batchOid,
  sampleLabel,
  method = null,
  permitLimit = null,
  permitWarn = null,
  onClose,
}: RoutePopupProps) {
  const [panels, setPanels] = useState<PanelData[] | null>(null);
  const [maximized, setMaximized] = useState(false);
  // When maximized, fill the CONTENT AREA (right of the sidebar, below the top
  // bar) — NOT the whole viewport, so the nav and header stay in view. Measured
  // from the shell's .content region, re-measured on resize.
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maximized) {
      setMaxRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector('.content') as HTMLElement | null;
      const r = el?.getBoundingClientRect();
      const pad = 8;
      if (r && r.width > 40) {
        setMaxRect({ top: r.top + pad, left: r.left + pad, width: r.width - pad * 2, height: r.height - pad * 2 });
      } else {
        // Fallback if the shell region can't be found — near-full viewport.
        setMaxRect({ top: 8, left: 8, width: window.innerWidth - 16, height: window.innerHeight - 16 });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maximized]);

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    setPanels(null);
    (async () => {
      const loaded = await Promise.all(
        route.panels.map(async (panel): Promise<PanelData> => {
          try {
            return panel.kind === 'qc'
              ? await loadQcPanel(route, panel, ctrl.signal)
              : panel.kind === 'surrogate'
                ? await loadSurrogatePanel(route, panel, ctrl.signal)
                : panel.kind === 'isresp'
                  ? await loadIsRespPanel(route, panel, ctrl.signal)
                  : await loadTrendPanel(route, panel, batchOid, panel.kind, { permitLimit, permitWarn }, ctrl.signal);
          } catch (e) {
            if (ctrl.signal.aborted) return { kind: 'err', label: panel.label, message: '' };
            const message = e instanceof Error ? e.message : 'Could not load this chart.';
            return { kind: 'err', label: panel.label, message };
          }
        }),
      );
      if (alive && !ctrl.signal.aborted) setPanels(loaded);
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [route, batchOid, permitLimit, permitWarn]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A client sample is NOT QC (v10 §1): its header shows the GROUPING used
  // (Site / Project · Method · Parameter), never a QC classification. Every other
  // route shows the QC chain the icon resolved on. The Site → Project fallback
  // (§1b): when the panel carries a project (no site), the header shows "Project"
  // + a "Project grouping" badge.
  const clientPanel = route.panels.find((p) => p.kind === 'client');
  const isProjectGrouped = !!clientPanel?.project;
  const groupNoun = isProjectGrouped ? 'Project' : 'Site';
  // §1c "No scope available": a client sample with NO Site and NO Project. The header
  // then shows only Method · Parameter (no grouping chip, no route arrow) and the body
  // is the honest "Historical Trend Not Available" empty state.
  const clientHasScope = !!(clientPanel && (clientPanel.site || clientPanel.project));
  const clientNoScope = !!clientPanel && !clientHasScope;
  // §3 Surrogate / §4 Internal Standard: not QC rows — the header carries no QC
  // classification, just the compound + method + the metric being trended, matching
  // the mock (Surrogate → % Recovery; Internal Standard → Response %).
  const isSurrogateRoute = route.classification === 'Surrogate';
  const isInternalStandardRoute = route.classification === 'InternalStandard';
  // §4 header context — the instrument the IS series ran on (dominant, "+N more" when
  // several). It comes from the LOADED panel data (not the route), so it appears once
  // the series has loaded and is filtered out when absent.
  const isInstrument = isInternalStandardRoute
    ? (panels?.find((pp): pp is SurrogatePanel => pp.kind === 'surrogate' && pp.variant === 'is')?.instrument ?? null)
    : null;
  // §3/§4 popup TITLE bar — the mock reads "Trend — Surrogate · {compound}" /
  // "Trend — Internal Std · {compound}" (a compound, not a sample row), NOT
  // "Trend — {sampleCode} · {compound}".
  const headerLabel = isSurrogateRoute
    ? `Surrogate · ${route.parameter ?? ''}`
    : isInternalStandardRoute
      ? `Internal Std · ${route.parameter ?? ''}`
      : sampleLabel;
  const chain: { k: string; v: string | null | undefined }[] = (
    clientPanel
      ? [
          { k: groupNoun, v: isProjectGrouped ? clientPanel.project : clientPanel.site },
          { k: 'Method', v: method },
          { k: 'Parameter', v: route.parameter },
        ]
      : isSurrogateRoute
      ? [
          { k: 'Surrogate', v: route.parameter },
          { k: 'Method', v: method },
          { k: 'Metric', v: '% Recovery' },
        ]
      : isInternalStandardRoute
      ? [
          // §4: an internal standard is method-driven — compound + method + the run's
          // instrument (dominant across the series) + the metric (Response %).
          { k: 'Internal Std', v: route.parameter },
          { k: 'Method', v: method },
          { k: 'Instrument', v: isInstrument },
          { k: 'Metric', v: 'Response %' },
        ]
      : [
          // §2 header — the essentials (QC Type · Method · Parameter · Site). The raw
          // QCRootRole / QCRole / QCSource classification AND the Pair (MS1 ↔ MSD1) are
          // the derivation, shown collapsed under "Admin / details" in the mock (a later
          // add) — NOT inline (the §2d prototype header shows no Pair chip).
          { k: 'QC Type', v: route.qcTypeName },
          { k: 'Method', v: method },
          { k: 'Parameter', v: route.parameter },
          // §2b: a sample duplicate carries its parent sample's Site as context (shown
          // only when available; filtered out otherwise).
          { k: 'Site', v: route.siteName },
        ]
  ).filter((c) => c.v);

  return createPortal(
    <div className="sdms-brp-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className={`sdms-brp trend-pop${maximized ? ' trend-pop-max' : ''}`}
        role="dialog"
        aria-label={`Trend — ${headerLabel}`}
        style={
          maximized && maxRect
            ? {
                position: 'fixed',
                top: maxRect.top,
                left: maxRect.left,
                width: maxRect.width,
                height: maxRect.height,
                maxWidth: 'none',
                margin: 0,
              }
            : undefined
        }
      >
        <div className="sdms-brp-hd trend-bar">
          <span>Trend — {headerLabel}</span>
          <div className="trend-bar-actions">
            <button
              type="button"
              className="trend-bar-btn"
              onClick={() => setMaximized((m) => !m)}
              title={maximized ? 'Restore' : 'Maximize'}
              aria-label={maximized ? 'Restore' : 'Maximize'}
            >
              <i className={`ti ${maximized ? 'ti-minimize' : 'ti-maximize'}`} aria-hidden />
            </button>
            <button type="button" className="trend-bar-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        <div className="sdms-brp-body">
          {/* Derivation — the chain the icon resolved on, so the route is never a
              black box (guide "Do not lose the derivation"). */}
          <div className="trend-hd">
            <div className="trend-hd-chain">
              {chain.map((c) => (
                <span key={c.k}>
                  {c.k}: <b>{c.v}</b>
                </span>
              ))}
              {!clientNoScope && <span className="trend-route">→ {route.chartLabel}</span>}
            </div>
            {clientPanel ? (
              // v10 §1 wording — names the analyte + grouping, bolds the key terms.
              // §1b: when grouped by project (no sampling location), say "Project".
              // §1c: no scope → no description box (the empty state speaks for itself).
              clientHasScope ? (
                isProjectGrouped ? (
                  // §1b wording — the Site → Project fallback, verbatim from the mock.
                  <div className="trend-hd-why">
                    This sample has <b>no Site</b>, so results are grouped by <b>Project</b> instead. Same
                    trend, vs the applicable limits, with the informational <b>Mean</b> of the displayed
                    results. (The Site→Project fallback happens automatically — the user just sees the
                    grouping in use.)
                  </div>
                ) : (
                  // §1 wording — names the analyte + Site, bolds the key terms.
                  <div className="trend-hd-why">
                    Trends historical <b>{route.parameter}</b> results for this <b>Site</b> against the
                    applicable reporting/regulatory limits (MCL shown). The <b>Mean</b> of the displayed
                    results is a thin informational line — it shows whether the current result is above or
                    below the site’s usual level, and never drives Warning / Out-of-control.
                  </div>
                )
              ) : null
            ) : route.chartLabel === 'Blank Result Trend' ? (
              // §2 wording — names the analyte and BOLDS the key terms (matches the mock).
              <div className="trend-hd-why">
                Trends the measured blank result for <b>{route.parameter}</b> to identify contamination,
                carryover, or rising background. Each blank is judged against the{' '}
                <b>method blank acceptance criterion</b> (the primary line) — a blank <b>above</b> it is
                out of control. The statistical Mean / ±2SD / ±3SD are shown only as an{' '}
                <b>optional, informational</b> band; they do not define pass/fail unless the method requires it.
              </div>
            ) : route.chartLabel === 'Duplicate RPD Trend' ? (
              // §2b wording — precision, one-sided, RPD Control Limit, Site context; bolded.
              <div className="trend-hd-why">
                Trends the RPD between the original sample and its duplicate to evaluate analytical{' '}
                <b>precision</b>. RPD is an absolute, non-negative, <b>one-sided</b> measure — there is no
                lower limit; closer to zero is better. Each RPD is compared with the configured{' '}
                <b>RPD Control Limit</b> (the dashed red line); an RPD <b>above</b> it is out of control.
                No warning line is shown unless one is explicitly configured for the method. Because a
                sample duplicate is a duplicate <i>of a client sample</i>, the <b>Site Name is shown when
                available</b> (from the parent sample) — it’s context only and does not change the RPD control.
              </div>
            ) : route.chartLabel === 'QC Recovery Trend' ? (
              // §2c wording — verbatim from the v10 prototype (accuracy vs the two-sided
              // Recovery control limits + optional 100% target, matrix-effect note, and the
              // paired MS/MSD ↔ RPD note); key terms bolded like the mock.
              <div className="trend-hd-why">
                Trends <b>%Recovery</b> against the configured <b>Recovery Lower and Upper Control
                Limits</b> (solid red), with an optional <b>100% target</b> (grey dashed). A result
                outside the limits may indicate matrix effects or analytical performance issues. For a
                paired MS/MSD or LCS/LCSD, an RPD panel is added against the RPD Control Limit.
              </div>
            ) : route.chartLabel === 'RPD Trend + Recovery Trend' ? (
              // §2d wording — verbatim from the v10 prototype: a spike duplicate is TWO
              // questions (precision + accuracy), both shown because a pair can pass one
              // and fail the other; key terms bolded like the mock.
              <div className="trend-hd-why">
                A spike duplicate answers <b>two questions</b>: precision (how well the pair agrees —
                RPD) and accuracy (did the spike recover — %Recovery). Both are shown, because a pair
                can pass one and fail the other. (LCS/LCSD is the same, evaluated against its own
                configured recovery limits.)
              </div>
            ) : route.chartLabel === 'Surrogate Recovery Trend' ? (
              // §3 wording — verbatim from the v10 prototype: %Recovery vs the configured
              // recovery limits, with point colours showing the sample category; bolded.
              <div className="trend-hd-why">
                Trends surrogate <b>%Recovery</b> over time against the configured recovery limits.{' '}
                <b>Point colours show the sample category</b> — low recovery in samples only points to
                matrix effects, while a dip everywhere points to the system.
              </div>
            ) : route.chartLabel === 'Internal Standard Response Trend' ? (
              // §4 wording — verbatim from the v10 prototype: Response % (Current ÷ Reference
              // × 100) vs the method-driven Lower/Upper acceptance limits (Test Code config);
              // bolded.
              <div className="trend-hd-why">
                Trends the Internal Standard <b>Response %</b> (Current ÷ Reference × 100, so 100% =
                Reference) over time against its <b>Lower and Upper Acceptance Limits</b> (e.g.
                50–150%) — <b>method-driven</b> and read from the Test Code config. A response shift may
                indicate changes in instrument performance, injection, or matrix effects. Point colours
                show the sample category.
              </div>
            ) : (
              WHY[route.chartLabel] && <div className="trend-hd-why">{WHY[route.chartLabel]}</div>
            )}
          </div>

          <div className="trend-panels">
            {panels === null ? (
              <div className="trend-loading">Loading chart…</div>
            ) : (
              panels.map((p, i) => (
                <div className="trend-panel" key={`${p.label}-${i}`}>
                  {/* §1c empty state stands alone — no "Sample Result Trend / Baseline"
                      header row above it (matches the mock). */}
                  {!(p.kind === 'client' && p.noScope) && (
                    <div className="trend-panel-hd">
                      <span>{p.label}</span>
                      {/* §1b: the "Project grouping" chip rides on the Sample Result Trend
                          TITLE row (next to Baseline), matching the mock — not in the header. */}
                      {p.kind === 'client' && isProjectGrouped && (
                        <span className="trend-group-badge">Project grouping</span>
                      )}
                      {p.kind !== 'err' && (
                        <span className={`trend-pill ${p.status}`}>{STATUS_LABEL[p.status]}</span>
                      )}
                    </div>
                  )}
                  {p.kind === 'err' ? (
                    <div className="trend-none">{p.message || 'Could not load this chart.'}</div>
                  ) : p.kind === 'qc' ? (
                    p.pts.length === 0 ? (
                      <div className="trend-none">No QC results in the last window to chart.</div>
                    ) : (
                      <LjChart pts={p.pts} st={p.st} field={p.field} theme={LIGHT} />
                    )
                  ) : p.kind === 'surrogate' ? (
                    p.data.length === 0 ? (
                      <div className="trend-none">No {p.variant === 'is' ? 'internal-standard responses' : 'surrogate recoveries'} in the last 12 months to chart.</div>
                    ) : (() => {
                      // v10 §3 (Surrogate) + §4 (Internal Standard) share this band chart but
                      // differ in the limit labels/style, the centre line, the legend and the
                      // caption. Surrogate = configured Recovery LCL/UCL SOLID + a 100% "Target";
                      // IS = method-driven Lower/Upper acceptance limits DASHED + a 100% "Reference".
                      const isIS = p.variant === 'is';
                      const V = isIS
                        ? { upper: 'Upper Limit', lower: 'Lower Limit', centre: 'Reference',
                            limitLegend: 'Lower / Upper Limit', centreLegend: 'Reference (100%)',
                            solid: false, metric: 'Response',
                            noun1: 'internal-standard response', nounN: 'internal-standard responses' }
                        : { upper: 'Recovery UCL', lower: 'Recovery LCL', centre: 'Target',
                            limitLegend: 'Recovery LCL / UCL', centreLegend: '100% target',
                            solid: true, metric: 'Recovery',
                            noun1: 'surrogate recovery', nounN: 'surrogate recoveries' };
                      const capBase = (isIS
                        ? `${p.data.length} run${p.data.length === 1 ? '' : 's'} · Response % (100% = Reference)` +
                          (p.low != null && p.high != null ? ` · method-driven limits ${trimZeros(p.low)} – ${trimZeros(p.high)}%` : '')
                        : `${p.data.length} run${p.data.length === 1 ? '' : 's'}` +
                          (p.low != null && p.high != null ? ` · limits ${trimZeros(p.low)} – ${trimZeros(p.high)}%` : '')
                      ) + (p.breached > 0 ? '' : '.');
                      return (
                      <>
                        {/* Legend — coloured DOTS = the parent-sample categories; the red lines =
                            the configured limits (SOLID for Recovery, DASHED for the IS acceptance
                            limits); the grey-dashed line = the 100% centre (Target / Reference). */}
                        <div className="trend-legend">
                          {p.cats.map((cat) => (
                            <span className="trend-leg-item" key={cat}>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: catColor(cat) }} />
                              {cat}
                            </span>
                          ))}
                          {(p.low != null || p.high != null) && (
                            <span className="trend-leg-item">
                              <span className="trend-leg-swatch" style={{ borderTopColor: LINE.permit, borderTopStyle: V.solid ? 'solid' : 'dashed' }} />
                              {V.limitLegend}
                            </span>
                          )}
                          <span className="trend-leg-item">
                            <span className="trend-leg-swatch" style={{ borderTopColor: '#94a3b8', borderTopStyle: 'dashed' }} />
                            {V.centreLegend}
                          </span>
                        </div>
                        <ResponsiveContainer width="100%" height={340}>
                          {/* Clean plot (prototype parity with §1/§2c): no grid, no Y-axis
                              numbers, no axis lines — the labelled limit lines + caption carry
                              the values, the chart shows the shape. */}
                          <LineChart data={p.data} margin={{ top: 18, right: 90, bottom: 44, left: 44 }}>
                            <XAxis
                              dataKey="label"
                              fontSize={11}
                              tick={{ fill: '#6b7280' }}
                              angle={-35}
                              textAnchor="end"
                              height={54}
                              interval={Math.max(0, Math.ceil(p.data.length / 12) - 1)}
                              axisLine={false}
                              tickLine={false}
                              padding={{ left: 12, right: 20 }}
                            />
                            <YAxis hide domain={[p.yMin, p.yMax]} allowDataOverflow={false} />
                            <Tooltip
                              formatter={(v: number, _n: unknown, item: { payload?: { cat?: string } }) => [
                                `${v}%`,
                                item?.payload?.cat ?? V.metric,
                              ]}
                              contentStyle={{ fontSize: 12 }}
                            />
                            {/* 100% centre — grey dashed, informational (Target / Reference). */}
                            <ReferenceLine y={100} stroke="#94a3b8" strokeDasharray="4 4"
                              label={{ value: `${V.centre} 100`, position: 'right', fontSize: 9.5, fill: '#94a3b8' }} />
                            {/* Configured limits — SOLID red for Recovery control limits, DASHED
                                red for the IS Lower/Upper acceptance limits. */}
                            {p.high != null && (
                              <ReferenceLine y={p.high} stroke={LINE.permit} strokeDasharray={V.solid ? undefined : '6 4'}
                                label={{ value: `${V.upper} ${trimZeros(p.high)}`, position: 'insideBottomRight', fontSize: 10, fill: LINE.permit }} />
                            )}
                            {p.low != null && (
                              <ReferenceLine y={p.low} stroke={LINE.permit} strokeDasharray={V.solid ? undefined : '6 4'}
                                label={{ value: `${V.lower} ${trimZeros(p.low)}`, position: 'insideTopRight', fontSize: 10, fill: LINE.permit }} />
                            )}
                            <Line
                              type="linear"
                              dataKey="v"
                              stroke="#c7ced8"
                              strokeWidth={1.4}
                              strokeDasharray="4 3"
                              isAnimationActive={false}
                              connectNulls
                              activeDot={{ r: 6 }}
                              // Colour each marker by the parent sample's category (Bin Yu
                              // v10) — a low surrogate clustered in one sample type reads as a
                              // matrix effect, not a method failure. An out-of-control point
                              // KEEPS its category colour but gets a bigger RED ring.
                              dot={(dp: { cx?: number; cy?: number; index?: number; payload?: { cat?: string; v?: number } }) => {
                                const { cx, cy, index, payload } = dp;
                                if (cx == null || cy == null) return <g key={index} />;
                                const v = payload?.v;
                                const out = (p.high != null && v != null && v > p.high) || (p.low != null && v != null && v < p.low);
                                return (
                                  <circle
                                    key={index}
                                    cx={cx}
                                    cy={cy}
                                    r={out ? 6 : 4}
                                    fill={catColor(payload?.cat ?? '')}
                                    stroke={out ? LINE.permit : '#fff'}
                                    strokeWidth={out ? 2.4 : 1}
                                  />
                                );
                              }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        {/* Caption — n runs · limits · then EITHER the out-of-limits clause
                            (RED, out of control) OR the approaching-the-limit clause (AMBER,
                            warning), matching the v10 mock. Breach wins over warning. */}
                        <div className="trend-caption">
                          {capBase}
                          {p.breached > 0 ? (
                            <span className="trend-caption-alert">
                              {` · ${p.breached} ${p.breached === 1 ? V.noun1 : V.nounN} outside the limits — out of control.`}
                            </span>
                          ) : p.warnLow + p.warnHigh > 0 ? (
                            <span className="trend-caption-warn">
                              {(() => {
                                const n = p.warnLow + p.warnHigh;
                                const where = p.warnLow && p.warnHigh ? 'the limits' : p.warnLow ? 'the lower limit' : 'the upper limit';
                                return ` · ${n} ${n === 1 ? V.noun1 : V.nounN} approaching ${where} (warning).`;
                              })()}
                            </span>
                          ) : null}
                        </div>
                      </>
                      );
                    })()
                  ) : p.kind === 'limit' ? (
                    // §2 config-limit trend — Blank Result / Duplicate RPD (one-sided upper) and
                    // QC Recovery (two-sided LCL/UCL + 100% target). Values (blue) / out-of-control
                    // (red), the configured limit(s) as the PRIMARY line (solid for Recovery,
                    // dashed for the one-sided acceptance line); the statistical Mean/±2SD/±3SD
                    // band is OPTIONAL (Blank only — RPD/Recovery show no band).
                    p.data.length === 0 ? (
                      <div className="trend-none">No {p.pointLabel.toLowerCase()} results in the last window to chart.</div>
                    ) : (
                      <>
                        <div className="trend-legend">
                          <span className="trend-leg-item">
                            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#3a7bd5' }} />
                            {p.pointLabel}
                          </span>
                          {/* Always show the Out-of-control key (v10 prototype) — the legend
                              explains the RED marker even on a run with no current breach. */}
                          <span className="trend-leg-item">
                            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#d92d20' }} />
                            {p.outLabel}
                          </span>
                          {(p.rl != null || p.lcl != null) && (
                            <span className="trend-leg-item">
                              <span className="trend-leg-swatch" style={{ borderTopColor: LINE.permit, borderTopStyle: p.solidLimit ? 'solid' : 'dashed' }} />
                              {p.limitLegend}
                            </span>
                          )}
                          {p.target != null && (
                            <span className="trend-leg-item">
                              <span className="trend-leg-swatch" style={{ borderTopColor: '#94a3b8', borderTopStyle: 'dashed' }} />
                              {trimZeros(p.target)}% target
                            </span>
                          )}
                          {p.showBand && p.mean != null && (
                            <span className="trend-leg-item">
                              <span className="trend-leg-swatch" style={{ borderTopColor: '#94a3b8', borderTopStyle: 'dashed' }} />
                              Mean · ±2SD · ±3SD (optional)
                            </span>
                          )}
                        </div>
                        <ResponsiveContainer width="100%" height={340}>
                          <LineChart data={p.data} margin={{ top: 18, right: 90, bottom: 44, left: 44 }}>
                            <XAxis
                              dataKey="label"
                              fontSize={11}
                              tick={{ fill: '#6b7280' }}
                              angle={-35}
                              textAnchor="end"
                              height={54}
                              // Many blanks (n can be 100+): show ~12 spaced date labels,
                              // not one per point (which overflow into an unreadable band).
                              interval={Math.max(0, Math.ceil(p.data.length / 12) - 1)}
                              axisLine={false}
                              tickLine={false}
                              padding={{ left: 12, right: 20 }}
                            />
                            {/* The axis is VISIBLE again (owner decision 2026-09-28). The
                                old chart hid it — "the labelled limit lines carry the values"
                                — which only held while those lines were far apart; once an
                                outlier squeezed them together there was no scale left to
                                read at all. With the frame now clamped to the band, the
                                ticks stay meaningful. */}
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                            <YAxis
                              domain={[p.yMin, p.yMax]}
                              allowDataOverflow
                              width={54}
                              fontSize={11}
                              tick={{ fill: '#6b7280' }}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={(v: number) => trimZeros(v)}
                            />
                            <Tooltip
                              // Always the TRUE value, never the clamped one.
                              formatter={(_v: number, _n: unknown, item: { payload?: { v?: number; clamped?: boolean } }) => [
                                `${trimZeros(item?.payload?.v ?? 0)}${p.units ? ` ${p.units}` : ''}` +
                                  (item?.payload?.clamped ? ' (off scale)' : ''),
                                p.pointLabel,
                              ]}
                              contentStyle={{ fontSize: 12 }}
                            />
                            {/* OPTIONAL statistical band (informational only, grey) — Blank only. */}
                            {p.showBand && p.plus3 != null && (
                              <ReferenceLine y={p.plus3} stroke="#c2ccd8" strokeDasharray="3 4"
                                label={{ value: `+3SD ${trimZeros(p.plus3)}`, position: 'right', fontSize: 9.5, fill: '#94a3b8' }} />
                            )}
                            {p.showBand && p.plus2 != null && (
                              <ReferenceLine y={p.plus2} stroke="#c2ccd8" strokeDasharray="3 4"
                                label={{ value: `+2SD ${trimZeros(p.plus2)}`, position: 'right', fontSize: 9.5, fill: '#94a3b8' }} />
                            )}
                            {p.showBand && p.mean != null && (
                              <ReferenceLine y={p.mean} stroke="#94a3b8" strokeDasharray="4 4"
                                label={{ value: `Mean ${trimZeros(p.mean)}`, position: 'right', fontSize: 9.5, fill: '#94a3b8' }} />
                            )}
                            {/* 100% target (Recovery) — grey dashed, informational. */}
                            {p.target != null && (
                              <ReferenceLine y={p.target} stroke="#94a3b8" strokeDasharray="4 4"
                                label={{ value: `Target ${trimZeros(p.target)}${p.units ? ` ${p.units}` : ''}`, position: 'right', fontSize: 9.5, fill: '#94a3b8' }} />
                            )}
                            {/* PRIMARY upper line — configured limit (Blank RL / RPD control / Recovery UCL).
                                Solid red for Recovery (a two-sided control limit), dashed for the
                                one-sided Blank/RPD acceptance line. */}
                            {p.rl != null && (
                              <ReferenceLine y={p.rl} stroke={LINE.permit} strokeDasharray={p.solidLimit ? undefined : '6 4'}
                                label={{ value: `${p.limitLabel} ${trimZeros(p.rl)}${p.units ? ` ${p.units}` : ''}`, position: 'insideBottomRight', fontSize: 10, fill: LINE.permit }} />
                            )}
                            {/* PRIMARY lower line — Recovery LCL only (two-sided). Solid red. */}
                            {p.lcl != null && (
                              <ReferenceLine y={p.lcl} stroke={LINE.permit} strokeDasharray={p.solidLimit ? undefined : '6 4'}
                                label={{ value: `${p.limitLabelLow} ${trimZeros(p.lcl)}${p.units ? ` ${p.units}` : ''}`, position: 'insideTopRight', fontSize: 10, fill: LINE.permit }} />
                            )}
                            <Line
                              type="linear"
                              // vPlot = the value CLAMPED into the frame; payload.v stays true.
                              dataKey="vPlot"
                              stroke="#c2d6f0"
                              strokeWidth={1}
                              connectNulls
                              isAnimationActive={false}
                              activeDot={{ r: 6 }}
                              dot={(dp: {
                                cx?: number; cy?: number; index?: number;
                                payload?: { out?: boolean; clamped?: boolean; v?: number };
                              }) => {
                                const { cx, cy, index, payload } = dp;
                                if (cx == null || cy == null) return <g key={index} />;
                                const out = !!payload?.out;
                                // OFF-SCALE: the point sits beyond the framed band, so it is
                                // pinned to the edge and drawn as an arrow carrying its REAL
                                // value — the excursion stays obvious without the band being
                                // crushed to fit it.
                                if (payload?.clamped) {
                                  const up = (payload.v ?? 0) > p.yMax;
                                  const tip = up ? cy - 2 : cy + 2;
                                  const base = up ? cy + 8 : cy - 8;
                                  // The value label sits OUTSIDE the triangle, AWAY from the
                                  // frame edge — an up-excursion's label goes ABOVE its triangle
                                  // (into the 18px top margin) so it clears the acceptance-limit
                                  // line and its "Blank Acceptance Limit" text sitting just below
                                  // the edge; a down-excursion's stays below. This stops the
                                  // pinned 46 / 48 labels from colliding with the limit label
                                  // (user report 2026-09-30).
                                  return (
                                    <g key={index}>
                                      <path
                                        d={`M${cx},${tip} L${cx - 5},${base} L${cx + 5},${base} Z`}
                                        fill="#d92d20" stroke="#fff" strokeWidth={0.8}
                                      />
                                      <text
                                        x={cx} y={up ? tip - 5 : base - 4}
                                        textAnchor="middle" fontSize={9.5} fill="#d92d20"
                                      >
                                        {trimZeros(payload.v ?? 0)}
                                      </text>
                                    </g>
                                  );
                                }
                                // Small blue dots keep a dense clean-blank run (many near 0)
                                // readable; an out-of-control blank is a bigger red marker.
                                return (
                                  <circle key={index} cx={cx} cy={cy} r={out ? 4 : 2.4}
                                    fill={out ? '#d92d20' : '#3a7bd5'} stroke="#fff" strokeWidth={out ? 1 : 0.5} />
                                );
                              }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        {(p.captionBase || p.captionAlert || p.captionTail) && (
                          <div className="trend-caption">
                            {p.captionBase}
                            {p.captionAlert && <span className="trend-caption-alert">{p.captionAlert}</span>}
                            {p.captionTail}
                            {/* Never let the chart imply the excursion was smaller than it was. */}
                            {(p.clampedHigh + p.clampedLow > 0) && (
                              <span className="trend-caption-alert">
                                {` · ${p.clampedHigh + p.clampedLow} point${p.clampedHigh + p.clampedLow === 1 ? '' : 's'} off scale, pinned to the edge with the actual value.`}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : p.kind === 'client' && p.noScope ? (
                    // §1c "No scope available" — honest empty state, never a chart that
                    // blends unrelated clients.
                    <div className="trend-empty">
                      <div className="trend-empty-title">Historical Trend Not Available</div>
                      <div className="trend-empty-msg">
                        No Site, Project, or other configured sample grouping is available for this
                        result, so there is no meaningful population to trend.
                      </div>
                    </div>
                  ) : p.data.length === 0 ? (
                    // Specific reason (pending validation · all non-detects · no
                    // collect date · out of window · none) from the backend diagnostic,
                    // falling back to the generic wording when it's unavailable.
                    <div className="trend-none">
                      {trendEmptyMessage(p.emptyReason, groupNoun, 12)}
                    </div>
                  ) : (
                    <>
                      {/* Legend — Result + the reference lines (MCL · Mean) so the
                          on-line value labels can stay compact. */}
                      {p.lines.some((l) => l.legend) && (
                        <div className="trend-legend">
                          <span className="trend-leg-item">
                            <span className="trend-leg-swatch" style={{ borderTopColor: '#e8670a' }} />
                            Result
                          </span>
                          {p.lines
                            .filter((l) => l.legend)
                            .map((l) => (
                              <span className="trend-leg-item" key={l.legend}>
                                <span
                                  className="trend-leg-swatch"
                                  style={{ borderTopColor: l.color, borderTopStyle: l.dash ? 'dashed' : 'solid' }}
                                />
                                {l.legend}
                              </span>
                            ))}
                        </div>
                      )}
                      <ResponsiveContainer width="100%" height={340}>
                        <LineChart data={p.data} margin={{ top: 18, right: 78, bottom: 44, left: 44 }}>
                          {/* Prototype §1 = a CLEAN plot: NO background grid, NO axis
                              lines, NO Y-axis numbers. The labeled MCL / Mean reference
                              lines + the caption underneath carry the values; the chart
                              itself shows the SHAPE (is the site drifting toward its
                              limit?). Owner 2026-09-09: "why this background / are the x,y
                              axis lines needed?" — they are not, per the mock. */}
                          <XAxis
                            dataKey="label"
                            fontSize={11}
                            tick={{ fill: '#6b7280' }}
                            angle={-35}
                            textAnchor="end"
                            height={54}
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                            // Inset the first/last points from the plot edges (prototype
                            // §1) so the line has breathing room; the wider left MARGIN
                            // (above) is what actually keeps the first rotated date label
                            // from clipping at the SVG edge.
                            padding={{ left: 12, right: 20 }}
                          />
                          {/* Y-axis hidden — domain kept for scaling only, nothing drawn. */}
                          <YAxis hide domain={[p.yMin ?? 0, p.yMax]} allowDataOverflow={false} />
                          <Tooltip
                            formatter={(v: number) => [`${v}${p.units ? ` ${p.units}` : ''}`, 'Result']}
                            contentStyle={{ fontSize: 12 }}
                          />
                          {p.lines.map((l, li) => (
                            <ReferenceLine
                              key={`${l.label}-${li}`}
                              y={l.value}
                              stroke={l.color}
                              strokeDasharray={l.dash}
                              label={{ value: l.label, position: l.labelPos ?? 'insideTopRight', fontSize: 10, fill: l.color }}
                            />
                          ))}
                          <Line
                            type="linear"
                            dataKey="v"
                            stroke="#e8670a"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#e8670a', strokeWidth: 0 }}
                            activeDot={{ r: 6 }}
                            connectNulls
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      {p.caption && <div className="trend-caption">{p.caption}</div>}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
